import Axios, { AxiosInstance } from 'axios';
import errorFaultHelper from 'utility/ErrorFaultHelpers';

export type HttpHeaders = {
  [key: string]: string;
};

export type RequestConfig = {
  headers?: HttpHeaders;
  skipAuth?: boolean;
};


export class ApiConfiguration {
  accessToken?: string;
  apiBaseUrl?:string;
}

export interface IApiClient {
    post<TRequest, TResponse>(
      path: string,
      object: TRequest,
      config?: RequestConfig,
      displayError?: boolean
    ): Promise<TResponse>;
    patch<TRequest, TResponse>(
      path: string,
      object: TRequest
    ): Promise<TResponse>;
    put<TRequest, TResponse>(path: string, object: TRequest): Promise<TResponse>;
    get<TResponse>(path: string, displayError?:boolean): Promise<TResponse>;
  }

  export default class ApiClient implements IApiClient {
    private client: AxiosInstance;
    protected createAxiosClient(
      apiConfiguration: ApiConfiguration
    ): AxiosInstance {
      return Axios.create({
        baseURL: apiConfiguration.apiBaseUrl,
        responseType: 'json' as const,
        headers: {
          'Content-Type': 'application/json',
          ...(apiConfiguration.accessToken && {
            Authorization: `Bearer ${apiConfiguration.accessToken}`,
          }),
        },
        timeout: 30 * 1000,
      });
    }
  
    constructor(apiConfiguration: ApiConfiguration) {
      this.client = this.createAxiosClient(apiConfiguration);
      //add access_token from localstorage
      this.client.interceptors.request.use((request) => {
        if ((request as any).skipAuth) {
          return request;
        }

        const key = Object.keys(localStorage).find(k => k.startsWith('oidc.user'));
        const parsedObject = key ? JSON.parse(localStorage.getItem(key) as string) : null;

        if (parsedObject?.access_token) {
          request.headers.Authorization = 'Bearer ' + parsedObject.access_token;
        }

        return request;
      }, (error) => Promise.reject(error));

    }

   
  
    async post<TRequest, TResponse>(
      path: string,
      payload: TRequest,
      config?: RequestConfig,
      displayError: boolean = true
    ): Promise<TResponse> {
      try {
        const response = config
          ? await this.client.post<TResponse>(path, payload, config)
          : await this.client.post<TResponse>(path, payload);
        return response.data;
      } catch (error) {
        if (displayError){
          const errorMsh = errorFaultHelper("Feilmelding", "alert", `Fikk ikke registrert data.`, error);
        }
      }
      return {} as TResponse;
    }
  
    async patch<TRequest, TResponse>(
      path: string,
      payload: TRequest
    ): Promise<TResponse> {
      try {
        const response = await this.client.patch<TResponse>(path, payload);
        return response.data;
      } catch (error) {
        errorFaultHelper("Error", "alert", "Patch funksjon kan ikke hente", error);
      }
      return {} as TResponse;
    }
  
    async put<TRequest, TResponse>(
      path: string,
      payload: TRequest
    ): Promise<TResponse> {
      try {
        const response = await this.client.put<TResponse>(path, payload);
        return response.data;
      } catch (error) {
        errorFaultHelper("Error", "alert", "", error);
      }
      return {} as TResponse;
    }
  
    async get<TResponse>(path: string, displayError: boolean = true): Promise<TResponse> {
      try {
        const response = await this.client.get<TResponse>(path);
        return response.data;
      } catch (error) {
        if (displayError)
        errorFaultHelper("Feilmelding", "alert", `Kunne ikke hente data.`, error);
      }
      return {} as TResponse;
    }
  }
